import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Phone, Plus, Power, Trash2, Copy, CheckCircle2, Wifi, WifiOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const IntegrationSettings = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState('');
  const [testingProvider, setTestingProvider] = useState(null);
  const [testResults, setTestResults] = useState({});

  const [providerForm, setProviderForm] = useState({
    name: 'knowlarity',
    display_name: '',
    api_key: '',
    api_secret: '',
    base_url: '',
    config: {},
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const providerTemplates = {
    knowlarity: {
      display_name: 'Knowlarity',
      webhook_path: '/api/webhooks/knowlarity',
      fields: ['api_key', 'api_secret'],
    },
    ozonetel: {
      display_name: 'Ozonetel',
      webhook_path: '/api/webhooks/ozonetel',
      fields: ['api_key', 'base_url'],
    },
    generic: {
      display_name: 'Custom Provider',
      webhook_path: '/api/webhooks/generic',
      fields: ['api_key', 'api_secret', 'base_url'],
    },
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      const response = await axios.get(`${API}/integrations/telephony`);
      setProviders(response.data);
    } catch (error) {
      console.error('Failed to fetch providers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProvider = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/integrations/telephony`, providerForm);
      toast.success('Integration created successfully!');
      setIsDialogOpen(false);
      setProviderForm({
        name: 'knowlarity',
        display_name: '',
        api_key: '',
        api_secret: '',
        base_url: '',
        config: {},
      });
      fetchProviders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create integration');
    }
  };

  const handleToggle = async (providerId) => {
    try {
      const response = await axios.put(`${API}/integrations/telephony/${providerId}/toggle`);
      toast.success(response.data.message);
      fetchProviders();
    } catch (error) {
      toast.error('Failed to toggle integration');
    }
  };

  const handleDelete = async (providerId) => {
    if (!window.confirm('Are you sure you want to delete this integration?')) return;

    try {
      await axios.delete(`${API}/integrations/telephony/${providerId}`);
      toast.success('Integration deleted successfully!');
      fetchProviders();
    } catch (error) {
      toast.error('Failed to delete integration');
    }
  };

  const testConnection = async (providerId) => {
    setTestingProvider(providerId);
    try {
      const response = await axios.post(`${API}/integrations/telephony/${providerId}/test`);
      setTestResults(prev => ({ ...prev, [providerId]: response.data }));
      if (response.data.status === 'success') {
        toast.success('Connection test successful!');
      } else {
        toast.error(`Test failed: ${response.data.message}`);
      }
    } catch (error) {
      setTestResults(prev => ({ 
        ...prev, 
        [providerId]: { status: 'error', message: error.response?.data?.detail || 'Test failed' } 
      }));
      toast.error('Connection test failed');
    } finally {
      setTestingProvider(null);
    }
  };

  const copyWebhookUrl = (path) => {
    const url = `${BACKEND_URL}${path}`;
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    toast.success('Webhook URL copied!');
    setTimeout(() => setCopiedUrl(''), 2000);
  };

  const getWebhookUrl = (providerName) => {
    const template = providerTemplates[providerName];
    return template ? `${BACKEND_URL}${template.webhook_path}` : '';
  };

  return (
    <Layout>
      <div className="space-y-8 fade-in" data-testid="integration-settings-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Telephony Integrations
            </h1>
            <p className="text-slate-600">Connect with cloud telephony providers for live call monitoring</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#4338CA] hover:bg-[#3730A3] text-white" data-testid="create-integration-button">
                <Plus size={20} className="mr-2" />
                Add Integration
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Add Telephony Integration</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateProvider} className="space-y-4" data-testid="integration-form">
                <div>
                  <Label htmlFor="provider-type">Provider Type</Label>
                  <Select
                    value={providerForm.name}
                    onValueChange={(value) =>
                      setProviderForm({
                        ...providerForm,
                        name: value,
                        display_name: providerTemplates[value].display_name,
                      })
                    }
                  >
                    <SelectTrigger id="provider-type" data-testid="provider-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="knowlarity">Knowlarity</SelectItem>
                      <SelectItem value="ozonetel">Ozonetel</SelectItem>
                      <SelectItem value="generic">Custom Provider</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="display-name">Display Name</Label>
                  <Input
                    id="display-name"
                    placeholder="e.g., Production Knowlarity"
                    value={providerForm.display_name}
                    onChange={(e) => setProviderForm({ ...providerForm, display_name: e.target.value })}
                    required
                    data-testid="display-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="api-key">API Key</Label>
                  <Input
                    id="api-key"
                    placeholder="Enter API key"
                    value={providerForm.api_key}
                    onChange={(e) => setProviderForm({ ...providerForm, api_key: e.target.value })}
                    required
                    data-testid="api-key-input"
                  />
                </div>

                {providerTemplates[providerForm.name]?.fields.includes('api_secret') && (
                  <div>
                    <Label htmlFor="api-secret">API Secret</Label>
                    <Input
                      id="api-secret"
                      type="password"
                      placeholder="Enter API secret"
                      value={providerForm.api_secret}
                      onChange={(e) => setProviderForm({ ...providerForm, api_secret: e.target.value })}
                      data-testid="api-secret-input"
                    />
                  </div>
                )}

                {providerTemplates[providerForm.name]?.fields.includes('base_url') && (
                  <div>
                    <Label htmlFor="base-url">Base URL (Optional)</Label>
                    <Input
                      id="base-url"
                      placeholder="https://api.provider.com"
                      value={providerForm.base_url}
                      onChange={(e) => setProviderForm({ ...providerForm, base_url: e.target.value })}
                      data-testid="base-url-input"
                    />
                  </div>
                )}

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm font-medium text-slate-900 mb-2">Webhook URL</p>
                  <p className="text-xs text-slate-600 mb-2">
                    Configure this URL in your {providerTemplates[providerForm.name].display_name} dashboard:
                  </p>
                  <div className="flex gap-2">
                    <code className="flex-1 p-2 bg-white border border-slate-200 rounded text-xs">
                      {getWebhookUrl(providerForm.name)}
                    </code>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => copyWebhookUrl(providerTemplates[providerForm.name].webhook_path)}
                    >
                      <Copy size={16} />
                    </Button>
                  </div>
                </div>

                <Button type="submit" className="w-full" data-testid="submit-integration-button">
                  Create Integration
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Integrations List */}
        {loading ? (
          <p className="text-slate-600">Loading integrations...</p>
        ) : providers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {providers.map((provider) => {
              const template = providerTemplates[provider.name];
              const webhookUrl = template ? `${BACKEND_URL}${template.webhook_path}` : '';
              const isCopied = copiedUrl === webhookUrl;

              return (
                <Card
                  key={provider.id}
                  className="p-6 border border-slate-200 shadow-sm"
                  data-testid={`provider-card-${provider.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-indigo-50 rounded-lg">
                        <Phone size={24} className="text-indigo-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900">{provider.display_name}</h3>
                        <p className="text-sm text-slate-600 capitalize">{provider.name}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => testConnection(provider.id)}
                        disabled={testingProvider === provider.id}
                        className="border-blue-300 text-blue-700 hover:bg-blue-50"
                        data-testid={`test-${provider.id}`}
                      >
                        {testingProvider === provider.id ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : testResults[provider.id]?.status === 'success' ? (
                          <Wifi size={16} className="text-emerald-600" />
                        ) : testResults[provider.id]?.status === 'error' ? (
                          <WifiOff size={16} className="text-rose-600" />
                        ) : (
                          <Wifi size={16} />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleToggle(provider.id)}
                        className={provider.is_active ? 'border-emerald-600 text-emerald-700' : 'border-slate-300'}
                        data-testid={`toggle-${provider.id}`}
                      >
                        <Power size={16} className={provider.is_active ? 'text-emerald-600' : ''} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(provider.id)}
                        className="border-rose-300 text-rose-700 hover:bg-rose-50"
                        data-testid={`delete-${provider.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <p className="text-xs font-semibold text-slate-700 mb-1">Status</p>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium border ${
                          provider.is_active
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : 'bg-slate-100 text-slate-700 border-slate-200'
                        }`}
                      >
                        {provider.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    <div>
                      <p className="text-xs font-semibold text-slate-700 mb-1">Webhook URL</p>
                      <div className="flex gap-2">
                        <code className="flex-1 p-2 bg-slate-50 border border-slate-200 rounded text-xs truncate">
                          {webhookUrl}
                        </code>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyWebhookUrl(template.webhook_path)}
                          data-testid={`copy-webhook-${provider.id}`}
                        >
                          {isCopied ? <CheckCircle2 size={14} className="text-emerald-600" /> : <Copy size={14} />}
                        </Button>
                      </div>
                    </div>

                    <div>
                      <p className="text-xs font-semibold text-slate-700 mb-1">API Key</p>
                      <code className="p-2 bg-slate-50 border border-slate-200 rounded text-xs block truncate">
                        {provider.api_key.substring(0, 20)}***
                      </code>
                    </div>

                    <div className="pt-2 border-t border-slate-200">
                      <p className="text-xs text-slate-500">
                        Created {new Date(provider.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="p-12 text-center border border-slate-200 shadow-sm">
            <Phone size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-600 mb-4">No integrations configured yet</p>
            <Button onClick={() => setIsDialogOpen(true)} data-testid="create-first-integration-button">
              Create First Integration
            </Button>
          </Card>
        )}

        {/* Integration Guide */}
        <Card className="p-6 border border-blue-200 bg-blue-50 shadow-sm">
          <h2 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Integration Guide
          </h2>
          <div className="space-y-3 text-sm text-slate-700">
            <p>
              <strong>Step 1:</strong> Create an integration above by selecting your telephony provider
            </p>
            <p>
              <strong>Step 2:</strong> Copy the webhook URL provided for your integration
            </p>
            <p>
              <strong>Step 3:</strong> In your telephony provider dashboard, configure the webhook URL to send events
            </p>
            <p>
              <strong>Step 4:</strong> Activate the integration by clicking the power button
            </p>
            <p className="pt-2 border-t border-blue-200">
              <strong>Events tracked:</strong> Call initiated, Call answered, Call ended, Recording available
            </p>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default IntegrationSettings;